import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFeatureFlagsFromContext } from '../../launchdarkly';
import { Box, Spinner, Text, Alert, AlertIcon, Button } from '@chakra-ui/react';
import { resolvePostLoginDestination } from '../../utils/returnPath';

/**
 * Component to handle Okta OAuth callback via BFF
 *
 * This component handles the redirect back from the BFF after successful authentication.
 * The BFF handles the actual OAuth flow with Okta, including:
 * - Token exchange
 * - Session creation
 * - Setting HttpOnly PA_AUTH cookie
 *
 * This component just needs to:
 * 1. Check for success/error parameters from BFF
 * 2. Set auth type in localStorage
 * 3. Redirect to appropriate page
 */
const OktaCallbackHandler = () => {
  const navigate = useNavigate();
  const flags = useFeatureFlagsFromContext();
  const showReskin = flags.showReskin || false;
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    const handleBFFCallback = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);

        // Check if we have an auth error in the URL (from BFF redirect)
        const authError = urlParams.get('authError');
        if (authError) {
          console.error('Authentication error from BFF:', authError);
          setError(decodeURIComponent(authError));
          setIsProcessing(false);
          return;
        }

        // Check for OAuth errors from Okta (if BFF redirected them to us)
        const oauthError = urlParams.get('error');
        const errorDescription = urlParams.get('error_description');
        if (oauthError) {
          console.error('OAuth error:', oauthError, errorDescription);
          setError(`Authentication failed: ${errorDescription || oauthError}`);
          setIsProcessing(false);
          return;
        }

        // Check if we have SSO success parameter (from BFF redirect)
        const ssoSuccess = urlParams.get('sso');
        if (ssoSuccess === 'true') {
          console.log('SSO authentication successful via BFF');

          // The PA_AUTH cookie is HttpOnly and set by the BFF
          // PA_BLID cookie indicates we're using SSO (no need to set auth_type in localStorage)

          // Resolve the post-login destination, in priority order:
          //   1. The return URL the BFF passed back (set at 401/session-expiry).
          //   2. A deep-link path we saved before redirecting to login (covers
          //      the case where the layout guard bounced the user to /login
          //      before any 401, so the BFF never saw the target page).
          //   3. The flag-based dashboard fallback.
          // Every candidate is validated with isSafeReturnPath to prevent an
          // open redirect from an attacker-controlled `redirect` query param.
          const rawReturnUrl = urlParams.get('redirect');
          let decodedReturnUrl = null;
          if (rawReturnUrl) {
            try {
              decodedReturnUrl = decodeURIComponent(rawReturnUrl);
            } catch (decodeError) {
              // A malformed `redirect` value (e.g. "?redirect=%E0%A4%A") makes
              // decodeURIComponent throw a URIError. Ignore it and fall back to
              // the saved path / default instead of failing the whole login.
              console.warn('Ignoring malformed redirect param:', decodeError);
            }
          }

          const destination = resolvePostLoginDestination({
            redirectParam: decodedReturnUrl,
            showReskin,
          });
          navigate(destination, { replace: true });
          return;
        }

        // If we reach here, we don't have expected parameters
        // This might mean the BFF is still processing, or something went wrong
        console.warn('No expected parameters found in callback URL');
        setError('Unexpected callback state. Please try signing in again.');
        setIsProcessing(false);
      } catch (err) {
        console.error('Error processing BFF callback:', err);
        setError(
          'An unexpected error occurred during authentication. Please try again.'
        );
        setIsProcessing(false);
      }
    };

    // Process the BFF callback
    handleBFFCallback();
  }, [navigate, showReskin]);

  // Handle error state with retry option
  const handleRetry = () => {
    // Redirect to login page to restart the flow
    navigate('/login', { replace: true });
  };

  // Handle manual redirect to login
  const handleGoToLogin = () => {
    navigate('/login', { replace: true });
  };

  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        padding={8}
      >
        <Alert status="error" maxWidth="500px" marginBottom={4}>
          <AlertIcon />
          <Box>
            <Text fontWeight="bold">Authentication Error</Text>
            <Text fontSize="sm">{error}</Text>
          </Box>
        </Alert>

        <Box display="flex" gap={4}>
          <Button onClick={handleRetry} colorScheme="blue">
            Try Again
          </Button>

          <Button onClick={handleGoToLogin} colorScheme="gray">
            Go to Login
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
    >
      <Spinner size="xl" color="blue.500" marginBottom={4} />
      <Text fontSize="lg" color="gray.600">
        {isProcessing ? 'Completing sign in...' : 'Redirecting...'}
      </Text>
    </Box>
  );
};

export default OktaCallbackHandler;
